import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { useWalletSupabaseSession } from "@/hooks/useWalletSupabaseSession";
import { useStrikeStatus } from "@/hooks/useStrikeStatus";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";
import { useEmonRate } from "@/contexts/EmonPriceContext";
import { useSolPrice } from "@/hooks/useSolPrice";
import { useIpfsUpload } from "@/hooks/useIpfsUpload";
import { useAirdropChallenge } from "@/hooks/useAirdropChallenge";

import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { HorizontalNFTScroll } from "@/components/dashboard/HorizontalNFTScroll";

import { StrikeBanner, NFTCreationWarning, AntiFraudCheckbox, RestrictedUserWarning } from "@/components/StrikeBanner";
import { EarlyCreatorNFTClaim } from "@/components/EarlyCreatorNFTClaim";
import { ClaimableBadges } from "@/components/ClaimableBadges";
import { CollectionManager } from "@/components/CollectionManager";
import { TelegramAnnouncementCard } from "@/components/TelegramAnnouncementCard";
import { NftInteractionsBar } from "@/components/marketplace/NftInteractionsBar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { 
  Upload, Wallet, Image, Coins, TrendingUp, DollarSign, 
  Trash2, User, Loader2, Pencil, X, Check, RefreshCw,
  ArrowDownToLine, ShieldCheck, ShieldAlert, Clock, AlertTriangle,
  Copy, Globe, Eye, Heart, HelpCircle, PauseCircle, PlayCircle,
  Award, ExternalLink
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type Profile = {
  id: string;
  display_name: string;
  avatar_url: string | null;
};

type LensReport = {
  analyzed_at: string;
  duplicate_score: number;
  is_duplicate: boolean;
  web_detection?: {
    full_matches?: Array<{ url: string }>;
    partial_matches?: Array<{ url: string }>;
    pages_with_image?: Array<{ url: string; title: string }>;
  };
  counts?: {
    full_matches: number;
    partial_matches: number;
    pages_with_image: number;
  };
  reasons?: string[];
};

type NFT = {
  id: string;
  name: string;
  image_url: string | null;
  price: number;
  current_price: number | null;
  total_earned: number;
  sales_count: number;
  description: string | null;
  is_listed: boolean | null;
  image_verified: boolean | null;
  image_check_status: 'pending' | 'approved' | 'rejected' | null;
  image_lens_report: LensReport | null;
  owner_wallet?: string;
  creator_wallet?: string;
  creator_name?: string;
};

type FavoriteNFT = {
  id: string;
  nft_id: string;
  nft: {
    id: string;
    name: string;
    image_url: string | null;
    price: number;
    current_price: number | null;
    owner_wallet: string;
    owner_name: string | null;
  } | null;
};

type Balance = {
  total_earned: number;
  available: number;
  withdrawn: number;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { publicKey, connected } = useWalletSafe();
  const { status: sessionStatus, retrySync } = useWalletSupabaseSession();
  const { strikeStatus, fraudStatus, combinedStatus, loading: strikeLoading } = useStrikeStatus();
  const { getComplianceData } = useDeviceFingerprint();
  const { rate: emonRate, usdToEmon } = useEmonRate();
  const { price: solPrice, usdToSol } = useSolPrice();
  const { uploadToIpfs, uploading: uploadingToIpfs, progress: ipfsProgress } = useIpfsUpload();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [favorites, setFavorites] = useState<FavoriteNFT[]>([]);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  // NFT creation
  const [newNFTName, setNewNFTName] = useState("");
  const [newNFTDescription, setNewNFTDescription] = useState("");
  const [newNFTCollection, setNewNFTCollection] = useState("");
  const [newNFTFile, setNewNFTFile] = useState<File | null>(null);
  const [creatingNFT, setCreatingNFT] = useState(false);
  
  const MAX_DESCRIPTION_LENGTH = 100;
  const MAX_COLLECTION_LENGTH = 50;
  const MIN_WITHDRAWAL_AMOUNT = 0.001; // Minimum 0.001 SOL to prevent spam
  
  // Avatar
  const [newAvatarFile, setNewAvatarFile] = useState<File | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  
  // Edit NFT
  const [editingNFT, setEditingNFT] = useState<string | null>(null);
  const [editNFTName, setEditNFTName] = useState("");
  const [savingNFT, setSavingNFT] = useState(false);
  
  // Toggle listing state
  const [togglingListing, setTogglingListing] = useState<string | null>(null);
  
  // Withdrawal
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  
  // Anti-fraud agreement
  const [antiFraudAgreed, setAntiFraudAgreed] = useState(false);
  
  // Collection manager refresh key
  const [collectionRefreshKey, setCollectionRefreshKey] = useState(0);

  const walletAddress = publicKey?.toBase58() || "";
  const shortWallet = walletAddress.slice(-5);

  // Post-mint share modal
  const [lastMintedNft, setLastMintedNft] = useState<{ id: string; name: string; image_url: string | null } | null>(null);
  const [shareCompleted, setShareCompleted] = useState(false);
  
  // Check if user is banned (from EITHER system)
  const isBanned = combinedStatus?.isAnyBanned ?? strikeStatus?.is_banned ?? false;
  const isImageBanned = combinedStatus?.isImageBanned ?? false;
  const isFraudBanned = combinedStatus?.isFraudBanned ?? false;
  const showFraudWarning = combinedStatus?.showFraudWarning ?? false;

  // AirDrop Challenge contest hook
  const { registerContestMint } = useAirdropChallenge(profile?.id, walletAddress);
  // Generate default display name from wallet
  const generateDisplayName = useCallback((wallet: string) => {
    return wallet.slice(-5).toUpperCase();
  }, []);

  // Re-entry detection: restore pending share state after returning from X
  useEffect(() => {
    const pendingData = localStorage.getItem("flow_share_pending_nft");
    if (!pendingData) return;

    try {
      const nftData = JSON.parse(pendingData) as {
        id: string;
        name: string;
        image_url: string | null;
      };
      setLastMintedNft(nftData);
      setShareCompleted(false);
      toast({ title: "Welcome back! Please complete the share on X." });
    } catch {
      localStorage.removeItem("flow_share_pending_nft");
    }
  }, []);

  useEffect(() => {
    if (!connected || !publicKey) {
      setLoading(false);
      return;
    }
    // Wait for a valid session before initializing — prevents fallback to walletAddress as profile ID
    if (sessionStatus !== "valid") {
      setLoading(true);
      return;
    }
    initDashboard();
  }, [connected, publicKey, sessionStatus]);

  // Realtime subscription for NFTs and balance
  useEffect(() => {
    if (!profile?.id || !walletAddress) return;

    const nftChannel = supabase
      .channel('nft-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'nfts',
        filter: `owner_wallet=eq.${walletAddress}`
      }, () => {
        fetchNFTs(profile.id);
      })
      .subscribe();

    const balanceChannel = supabase
      .channel('balance-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_balances',
        filter: `user_id=eq.${profile.id}`
      }, (payload) => {
        if (payload.new) {
          setBalance(payload.new as Balance);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(nftChannel);
      supabase.removeChannel(balanceChannel);
    };
  }, [profile?.id, walletAddress]);

  const initDashboard = async () => {
    setLoading(true);
    const wallet = publicKey!.toBase58();
    const defaultName = generateDisplayName(wallet);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // Session should be valid at this point (gated by useEffect above).
        // If still null, don't create a fake profile — just stop loading and wait.
        console.warn("[Dashboard] No user in session despite valid status — retrying sync");
        retrySync?.();
        setLoading(false);
        return;
      }

      // Fetch or create profile
      const { data: profileData } = await supabase
        .from("profiles")
        .select("id, display_name, avatar_url")
        .eq("id", user.id)
        .maybeSingle();

      if (!profileData) {
        await supabase.from("profiles").insert({
          id: user.id,
          wallet_address: wallet,
          display_name: defaultName,
        });
        setProfile({ id: user.id, display_name: defaultName, avatar_url: null });
        setDisplayName(defaultName);
      } else {
        const name = profileData.display_name || defaultName;
        setProfile({
          id: profileData.id,
          display_name: name,
          avatar_url: profileData.avatar_url,
        });
        setDisplayName(name);
      }

      // Link wallet
      await supabase.from("user_wallets").upsert({
        user_id: user.id,
        wallet_address: wallet,
      });

      // Fetch or create balance
      await fetchBalance(user.id);
      
      // Fetch NFTs
      await fetchNFTs(user.id);
      
      // Fetch favorites
      await fetchFavorites(user.id);

      // Log transaction
      await logTransaction(user.id, wallet, 'dashboard_login', 'profile', user.id);

    } catch (error) {
      console.error("Dashboard init error:", error);
      toast({ title: "Error loading dashboard", variant: "destructive" });
    }

    setLoading(false);
  };

  const fetchBalance = async (userId: string) => {
    const { data: balanceData } = await supabase
      .from("user_balances")
      .select("total_earned, available, withdrawn")
      .eq("user_id", userId)
      .maybeSingle();

    if (!balanceData) {
      await supabase.from("user_balances").insert({ user_id: userId });
      setBalance({ total_earned: 0, available: 0, withdrawn: 0 });
    } else {
      setBalance(balanceData);
    }
  };

  const fetchNFTs = async (userId: string) => {
    // Fetch all NFTs owned by this wallet (both created and purchased)
    const { data: nftData } = await supabase
      .from("nfts")
      .select("id, name, image_url, price, current_price, total_earned, sales_count, description, is_listed, image_verified, image_check_status, image_lens_report, owner_wallet, creator_wallet, creator_name")
      .eq("owner_wallet", walletAddress)
      .order("created_at", { ascending: false });

    setNfts((nftData || []) as NFT[]);
  };

  const fetchFavorites = async (userId: string) => {
    setLoadingFavorites(true);
    try {
      const { data: likesData } = await supabase
        .from("nft_likes")
        .select(`
          id,
          nft_id,
          nft:nfts(id, name, image_url, price, current_price, owner_wallet, owner_name)
        `)
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      setFavorites((likesData || []) as unknown as FavoriteNFT[]);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    }
    setLoadingFavorites(false);
  };

  const logTransaction = async (
    userId: string, 
    wallet: string, 
    action: string, 
    entityType?: string, 
    entityId?: string,
    details?: Record<string, unknown>
  ) => {
    try {
      // Get device/IP compliance data
      const complianceData = getComplianceData();
      
      const logData: {
        user_id: string;
        wallet_address: string;
        action_type: string;
        entity_type?: string;
        entity_id?: string;
        details?: Record<string, unknown>;
      } = {
        user_id: userId,
        wallet_address: wallet,
        action_type: action,
      };
      if (entityType) logData.entity_type = entityType;
      if (entityId) logData.entity_id = entityId;
      
      // Merge compliance data with details
      logData.details = {
        ...details,
        _compliance: complianceData,
      };
      
      await supabase.from("transaction_logs").insert(logData as any);
    } catch (e) {
      console.error("Log transaction error:", e);
    }
  };

  const updateProfile = async () => {
    if (!profile || !displayName.trim()) return;
    setSaving(true);
    
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ display_name: displayName.trim() })
        .eq("id", profile.id);

      if (error) throw error;
      
      setProfile({ ...profile, display_name: displayName.trim() });
      await logTransaction(profile.id, walletAddress, 'profile_update', 'profile', profile.id, { display_name: displayName.trim() });
      toast({ title: "Profile updated successfully" });
    } catch (error) {
      console.error("Update profile error:", error);
      toast({ title: "Failed to update profile", variant: "destructive" });
    }
    
    setSaving(false);
  };

  const handleAvatarUpload = async () => {
    if (!profile || !newAvatarFile) return;
    setUploadingAvatar(true);

    try {
      // Delete old avatar if exists
      if (profile.avatar_url) {
        // Extract the full path including folder structure
        const urlParts = profile.avatar_url.split("/storage/v1/object/public/avatars/");
        const oldPath = urlParts[1] ? decodeURIComponent(urlParts[1]) : null;
        if (oldPath) {
          await supabase.storage.from("avatars").remove([oldPath]);
        }
      }

      const fileExt = newAvatarFile.name.split(".").pop();
      // Use folder structure: {user_id}/{filename} for RLS policy compliance
      const fileName = `${profile.id}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, newAvatarFile);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      const publicUrl = urlData.publicUrl;

      await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", profile.id);

      setProfile({ ...profile, avatar_url: publicUrl });
      setNewAvatarFile(null);
      await logTransaction(profile.id, walletAddress, 'avatar_upload', 'profile', profile.id);
      toast({ title: "Avatar uploaded successfully" });
    } catch (error) {
      console.error("Avatar upload error:", error);
      toast({ title: "Failed to upload avatar", variant: "destructive" });
    }

    setUploadingAvatar(false);
  };

  const deleteAvatar = async () => {
    if (!profile?.avatar_url) return;

    try {
      // Extract the full path including folder structure
      const urlParts = profile.avatar_url.split("/storage/v1/object/public/avatars/");
      const filePath = urlParts[1] ? decodeURIComponent(urlParts[1]) : profile.avatar_url.split("/").slice(-2).join("/");
      await supabase.storage.from("avatars").remove([filePath]);
      await supabase.from("profiles").update({ avatar_url: null }).eq("id", profile.id);
      setProfile({ ...profile, avatar_url: null });
      await logTransaction(profile.id, walletAddress, 'avatar_delete', 'profile', profile.id);
      toast({ title: "Avatar deleted" });
    } catch (error) {
      console.error("Delete avatar error:", error);
      toast({ title: "Failed to delete avatar", variant: "destructive" });
    }
  };

  const createNFT = async () => {
    if (!profile || !newNFTFile) {
      toast({ title: "Please select an image for your NFT", variant: "destructive" });
      return;
    }

    // Check if user is banned
    if (isBanned) {
      toast({ 
        title: "Account Suspended", 
        description: "You cannot create NFTs while your account is suspended.",
        variant: "destructive" 
      });
      return;
    }

    setCreatingNFT(true);

    try {
      const nameToUse = newNFTName.trim() || `${profile.display_name}'s NFT`;
      
      // Upload to IPFS via NFT.Storage
      const uploadResult = await uploadToIpfs(newNFTFile);
      
      if (!uploadResult.success || !uploadResult.url) {
        throw new Error(uploadResult.error || 'Failed to upload to IPFS');
      }

      const publicUrl = uploadResult.url;
      console.log('NFT image uploaded to IPFS:', publicUrl);
      
      // Base price is $1.00 USD as defined in the bonding curve contract
      const initialPriceUsd = 1.0;

      const { data: newNFT, error: insertError } = await supabase
        .from("nfts")
        .insert({
          name: nameToUse,
          description: newNFTDescription.trim() || null,
          collection_name: newNFTCollection.trim() || null,
          image_url: publicUrl,
          // CREATOR fields - IMMUTABLE, set once at creation
          creator_wallet: walletAddress,
          creator_name: profile.display_name,
          // OWNER fields - DYNAMIC, changes on each sale
          owner_id: profile.id,
          owner_wallet: walletAddress,
          owner_name: profile.display_name,
          // Bonding curve pricing - USD is reference value
          price: initialPriceUsd,
          current_price: initialPriceUsd,
          base_price: initialPriceUsd,
          total_earned: 0,
          sales_count: 0,
          is_listed: true,
          image_verified: false,
          image_check_status: 'pending',
        })
        .select("id, name, image_url, price, current_price, total_earned, sales_count, description, is_listed, image_verified, image_check_status, image_lens_report")
        .single();

      if (insertError) throw insertError;

      if (newNFT) {
        setNfts([newNFT as NFT, ...nfts]);
        
        // Log ownership history
        await supabase.from("nft_ownership_history").insert({
          nft_id: newNFT.id,
          from_wallet: null,
          to_wallet: walletAddress,
          price: 0,
          transaction_type: 'mint'
        });
        
        // Log transaction
        await logTransaction(profile.id, walletAddress, 'nft_create', 'nft', newNFT.id, { name: nameToUse, image_url: uploadResult.url });

        // Trigger image verification in background
        supabase.functions.invoke('verify-nft-image', {
          body: { nft_id: newNFT.id, image_url: publicUrl }
        }).then(({ data, error }) => {
          if (error) {
            console.error("Image verification error:", error);
          } else {
            console.log("Image verification result:", data);
          }
        });

        // Show post-mint share modal
        setLastMintedNft({ id: newNFT.id, name: newNFT.name, image_url: newNFT.image_url });
        setShareCompleted(false);

        // Register for AirDrop Challenge contest (no-op if contest not active)
        registerContestMint(newNFT.id).catch(console.error);
      }
      
      setNewNFTFile(null);
      setNewNFTName("");
      setNewNFTDescription("");
      setNewNFTCollection("");
      // Refresh collection manager to show newly created NFT
      setCollectionRefreshKey(prev => prev + 1);
      toast({ title: "NFT created on IPFS! Image verification in progress..." });
    } catch (error) {
      console.error("Create NFT error:", error);
      toast({ title: "Failed to create NFT", variant: "destructive" });
    }

    setCreatingNFT(false);
  };

  const startEditNFT = (nft: NFT) => {
    setEditingNFT(nft.id);
    setEditNFTName(nft.name);
  };

  const cancelEditNFT = () => {
    setEditingNFT(null);
    setEditNFTName("");
  };

  const saveNFTEdit = async (nftId: string) => {
    if (!editNFTName.trim() || !profile) return;
    setSavingNFT(true);

    try {
      const { error } = await supabase
        .from("nfts")
        .update({ name: editNFTName.trim() })
        .eq("id", nftId);

      if (error) throw error;

      setNfts(nfts.map(n => n.id === nftId ? { ...n, name: editNFTName.trim() } : n));
      await logTransaction(profile.id, walletAddress, 'nft_edit', 'nft', nftId, { new_name: editNFTName.trim() });
      toast({ title: "NFT updated" });
      cancelEditNFT();
    } catch (error) {
      console.error("Edit NFT error:", error);
      toast({ title: "Failed to update NFT", variant: "destructive" });
    }

    setSavingNFT(false);
  };

  const toggleNFTListing = async (nft: NFT) => {
    if (!profile) return;
    setTogglingListing(nft.id);

    try {
      const newIsListed = !nft.is_listed;
      const { error } = await supabase
        .from("nfts")
        .update({ is_listed: newIsListed })
        .eq("id", nft.id);

      if (error) throw error;

      setNfts(nfts.map(n => n.id === nft.id ? { ...n, is_listed: newIsListed } : n));
      await logTransaction(profile.id, walletAddress, 'nft_listing_toggle', 'nft', nft.id, { 
        is_listed: newIsListed,
        action: newIsListed ? 'resumed_sale' : 'suspended_sale'
      });
      toast({ 
        title: newIsListed ? "NFT Listed for Sale" : "Sale Suspended",
        description: newIsListed 
          ? `"${nft.name}" is now available in the marketplace.`
          : `"${nft.name}" is now on hold. You can resume selling anytime.`
      });
    } catch (error) {
      console.error("Toggle listing error:", error);
      toast({ title: "Failed to update listing status", variant: "destructive" });
    }

    setTogglingListing(null);
  };

  const deleteNFT = async (nft: NFT) => {
    if (!profile) return;

    try {
      // Delete image from storage
      if (nft.image_url) {
        const urlParts = nft.image_url.split("/storage/v1/object/public/nft-images/");
        const filePath = urlParts[1] ? decodeURIComponent(urlParts[1]) : null;
        if (filePath) {
          await supabase.storage.from("nft-images").remove([filePath]);
        }
      }

      const { error } = await supabase
        .from("nfts")
        .delete()
        .eq("id", nft.id);

      if (error) throw error;

      setNfts(nfts.filter(n => n.id !== nft.id));
      await logTransaction(profile.id, walletAddress, 'nft_delete', 'nft', nft.id, { name: nft.name });
      toast({ title: "NFT deleted" });
    } catch (error) {
      console.error("Delete NFT error:", error);
      toast({ title: "Failed to delete NFT", variant: "destructive" });
    }
  };

  const requestWithdrawal = async () => {
    if (!profile || !balance) return;
    
    // Check if user is banned (basic client-side check, edge function does full check)
    if (isBanned) {
      toast({ 
        title: "Account Suspended", 
        description: "You cannot request withdrawals while your account is suspended.",
        variant: "destructive" 
      });
      return;
    }
    
    const amount = parseFloat(withdrawAmount);
    
    // Strict validation for amount
    if (isNaN(amount) || amount <= 0 || !isFinite(amount)) {
      toast({ title: "Enter a valid amount", variant: "destructive" });
      return;
    }
    
    // Minimum withdrawal check to prevent spam
    if (amount < MIN_WITHDRAWAL_AMOUNT) {
      toast({ 
        title: "Amount too small", 
        description: `Minimum withdrawal is $${MIN_WITHDRAWAL_AMOUNT} USD`,
        variant: "destructive" 
      });
      return;
    }
    
    // Round to 4 decimals to prevent floating point exploits
    const roundedAmount = Math.floor(amount * 10000) / 10000;
    
    if (roundedAmount > balance.available) {
      toast({ title: "Insufficient balance", variant: "destructive" });
      return;
    }

    setWithdrawing(true);

    try {
      // Get fingerprint for anti-fraud check
      const complianceData = getComplianceData();
      
      // Call edge function for auto-approval with fraud checks
      const { data, error } = await supabase.functions.invoke('process-withdrawal', {
        body: {
          amount: roundedAmount,
          wallet_address: walletAddress,
          user_id: profile.id,
          fingerprint: complianceData
        }
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        // Handle specific error cases
        if (data.error?.includes('banned') || data.error?.includes('suspended')) {
          toast({ 
            title: "Account Suspended", 
            description: data.error,
            variant: "destructive" 
          });
        } else if (data.error?.includes('frozen')) {
          toast({ 
            title: "Account Frozen", 
            description: "Your account is frozen. Contact support.",
            variant: "destructive" 
          });
        } else if (data.error?.includes('Insufficient')) {
          toast({ 
            title: "Insufficient Balance", 
            description: data.error,
            variant: "destructive" 
          });
        } else if (data.error?.includes('security') || data.error?.includes('blocked')) {
          toast({ 
            title: "Security Alert", 
            description: data.error,
            variant: "destructive" 
          });
        } else {
          toast({ 
            title: "Withdrawal Failed", 
            description: data.error || "Please try again later.",
            variant: "destructive" 
          });
        }
        setWithdrawing(false);
        return;
      }

      // Success!
      if (data.status === 'approved') {
        toast({ 
          title: "Withdrawal Approved!", 
          description: `$${roundedAmount.toFixed(2)} has been processed.`
        });
        // Refresh balance
        await fetchBalance(profile.id);
      } else if (data.status === 'pending_review') {
        toast({ 
          title: "Withdrawal Pending Review", 
          description: "Your withdrawal is being reviewed for security. You'll be notified when processed."
        });
      }

      setWithdrawAmount("");
      
      await logTransaction(profile.id, walletAddress, 'withdrawal_request', 'balance', profile.id, { 
        amount: roundedAmount,
        status: data.status,
        withdrawal_id: data.withdrawal_id
      });
      
    } catch (error) {
      console.error("Withdrawal error:", error);
      toast({ title: "Failed to process withdrawal", variant: "destructive" });
    }

    setWithdrawing(false);
  };

  // Not connected state
  if (!connected) {
    return (
      <div className="min-h-screen flex flex-col bg-background relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.15),transparent_40%),radial-gradient(circle_at_70%_80%,hsl(var(--accent)/0.1),transparent_40%)] pointer-events-none" />
        <AppHeader currentPage="dashboard" />
        <main className="flex-1 flex items-center justify-center px-4 pt-20 pb-24 relative z-10">
          <Card className="max-w-md w-full text-center p-8 border-border/50 bg-card/80 backdrop-blur-sm">
            <Wallet className="w-16 h-16 mx-auto mb-4 text-primary" />
            <h2 className="text-2xl font-bold mb-2">Connect Your Wallet</h2>
            <p className="text-muted-foreground">
              Please connect your wallet to access your FLOW dashboard.
            </p>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.15),transparent_40%),radial-gradient(circle_at_70%_80%,hsl(var(--accent)/0.1),transparent_40%)] pointer-events-none" />
        <AppHeader currentPage="dashboard" />
        <main className="flex-1 flex items-center justify-center pt-20 pb-24 relative z-10">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.15),transparent_40%),radial-gradient(circle_at_70%_80%,hsl(var(--accent)/0.1),transparent_40%)] pointer-events-none" />
      <AppHeader currentPage="dashboard" />
      
      <main className="flex-1 w-full max-w-6xl mx-auto px-3 sm:px-4 pt-20 pb-24 space-y-4 sm:space-y-6 relative z-10">
        {/* Strike/Ban Status Banner - SYSTEM 1: Image/Copyright */}
        {strikeStatus && (strikeStatus.is_banned || strikeStatus.strike_count > 0) && (
          <StrikeBanner
            strikeCount={strikeStatus.strike_count}
            isBanned={strikeStatus.is_banned}
            banExpiresAt={strikeStatus.ban_expires_at}
            lastStrikeReason={strikeStatus.last_strike_reason}
            banReason={strikeStatus.ban_reason}
          />
        )}
        
        {/* SYSTEM 2: Anti-Fraud Warning for restricted users */}
        {showFraudWarning && !isFraudBanned && fraudStatus && (
          <RestrictedUserWarning strikes={fraudStatus.strikes} />
        )}
        
        {/* SYSTEM 2: Anti-Fraud Banned */}
        {isFraudBanned && (
          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/50">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-destructive" />
              <div>
                <h3 className="font-semibold text-destructive">Account Permanently Banned</h3>
                <p className="text-sm text-muted-foreground">
                  Your account has been permanently suspended for fraud. All assets are frozen.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-0.5 sm:space-y-1">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">My Dashboard</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Manage your profile, NFTs, and earnings</p>
          </div>
          <Button variant="outline" size="sm" onClick={initDashboard} className="gap-2 w-full sm:w-auto">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        <div className="grid gap-6 md:gap-8 grid-cols-1 lg:grid-cols-3">
          {/* Left Column - Profile & Balance */}
          <div className="space-y-6">
            {/* Profile Card */}
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Avatar Section */}
                <div className="flex flex-col items-center gap-4">
                  <div className="relative group">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="Avatar"
                        className="w-24 h-24 rounded-full object-cover ring-4 ring-primary/20 transition-all group-hover:ring-primary/40"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center ring-4 ring-primary/20">
                        <User className="w-10 h-10 text-muted-foreground" />
                      </div>
                    )}
                    {profile?.avatar_url && (
                      <button
                        onClick={deleteAvatar}
                        className="absolute -top-2 -right-2 p-1.5 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:scale-110"
                        title="Delete avatar"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  
                  <div className="w-full space-y-2">
                    <label className="flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                      <Upload className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {newAvatarFile ? newAvatarFile.name : "Choose avatar"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => setNewAvatarFile(e.target.files?.[0] || null)}
                      />
                    </label>
                    {newAvatarFile && (
                      <Button
                        onClick={handleAvatarUpload}
                        disabled={uploadingAvatar}
                        className="w-full"
                        size="sm"
                      >
                        {uploadingAvatar ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                        ) : (
                          "Upload Avatar"
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Display Name */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Display Name</label>
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter display name"
                    className="bg-background/50"
                  />
                  <Button
                    onClick={updateProfile}
                    disabled={saving || displayName === profile?.display_name}
                    className="w-full"
                    size="sm"
                  >
                    {saving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                      "Save Name"
                    )}
                  </Button>
                </div>

                {/* Wallet Address - Horizontal layout with copy */}
                <div className="pt-4 border-t border-border/50 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground flex-shrink-0">Wallet</p>
                    <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
                      <p className="font-mono text-sm truncate text-foreground/80">{walletAddress}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0"
                        onClick={() => {
                          navigator.clipboard.writeText(walletAddress);
                          toast({ title: "Wallet copied!" });
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  
                </div>
              </CardContent>
            </Card>

            {/* Early Creator NFT Claim Card */}
            <EarlyCreatorNFTClaim />

            {/* Claimable Badges - shows badges earned but not yet claimed */}
            <ClaimableBadges />

            {/* Portfolio Value Card - Real-time NFT holdings */}
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Portfolio Value
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="w-4 h-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs max-w-xs">Real-time value of all NFTs you own, calculated from current market prices.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Total Portfolio Value */}
                <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl border border-primary/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <Coins className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Holdings</p>
                        <p className="text-2xl font-bold text-foreground">
                          ${nfts.reduce((sum, nft) => sum + (nft.current_price || nft.price), 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">≈ SOL</p>
                      <p className="text-lg font-semibold text-primary">
                        {usdToSol(nfts.reduce((sum, nft) => sum + (nft.current_price || nft.price), 0)).toFixed(4)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* NFT Count */}
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">NFTs Owned</span>
                  </div>
                  <span className="font-bold text-lg">{nfts.length}</span>
                </div>

                {/* Average NFT Value */}
                {nfts.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-accent/5 rounded-lg">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-accent" />
                      <span className="text-sm text-muted-foreground">Avg. NFT Value</span>
                    </div>
                    <span className="font-bold text-lg">
                      ${(nfts.reduce((sum, nft) => sum + (nft.current_price || nft.price), 0) / nfts.length).toFixed(2)}
                    </span>
                  </div>
                )}

                {/* Total Earned from Sales */}
                {balance && balance.total_earned > 0 && (
                  <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-primary" />
                      <span className="text-sm text-muted-foreground">Earned from Sales</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-lg">${balance.total_earned.toFixed(2)}</span>
                      <p className="text-xs text-muted-foreground">≈ {usdToSol(balance.total_earned).toFixed(4)} SOL</p>
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {nfts.length === 0 && (
                  <div className="text-center py-6">
                    <Image className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">No NFTs in your portfolio yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Create or purchase NFTs to see your holdings here</p>
                  </div>
                )}

                {/* Real-time indicator */}
                <div className="flex items-center justify-center gap-2 pt-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs text-muted-foreground">Real-time values</span>
                </div>
              </CardContent>
            </Card>

            {/* Collection Manager */}
            <CollectionManager 
              walletAddress={walletAddress} 
              onCollectionChange={() => fetchNFTs(profile?.id || "")}
              refreshKey={collectionRefreshKey}
            />

            {/* Telegram Announcement */}
            <TelegramAnnouncementCard />

            {/* Community Card */}
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardContent className="py-4">
                <a 
                  href="https://x.com/i/communities/1998535900641509521" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-foreground flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-background" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      Join the Community
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      Connect with other creators on X
                    </p>
                  </div>
                  <Globe className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                </a>
              </CardContent>
            </Card>

            {/* Badges & How to Earn Card */}
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardContent className="py-4">
                <Link 
                  to="/early-creators"
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground group-hover:text-amber-400 transition-colors">
                      Badges & How to Earn
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      View achievements and earn rewards
                    </p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-amber-400 transition-colors flex-shrink-0" />
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - NFTs */}
          <div className="lg:col-span-2 space-y-6">
            {/* Create NFT Card */}
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-primary" />
                  Create New NFT
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      NFT Name <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <Input
                      value={newNFTName}
                      onChange={(e) => setNewNFTName(e.target.value)}
                      placeholder={`${profile?.display_name || shortWallet}'s NFT`}
                      className="bg-background/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">NFT Image</label>
                    <label className="flex items-center justify-center gap-2 h-10 px-4 border border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                      <Upload className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground truncate">
                        {newNFTFile ? newNFTFile.name : "Choose image"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => setNewNFTFile(e.target.files?.[0] || null)}
                      />
                    </label>
                  </div>
                </div>
                
                {/* Description field with character limit */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">
                      Description <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <span className={`text-xs ${newNFTDescription.length > MAX_DESCRIPTION_LENGTH ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {newNFTDescription.length}/{MAX_DESCRIPTION_LENGTH}
                    </span>
                  </div>
                  <Input
                    value={newNFTDescription}
                    onChange={(e) => setNewNFTDescription(e.target.value)}
                    placeholder="Short description..."
                    className={`bg-background/50 ${newNFTDescription.length > MAX_DESCRIPTION_LENGTH ? 'border-destructive' : ''}`}
                    maxLength={MAX_DESCRIPTION_LENGTH + 10}
                  />
                  {newNFTDescription.length > MAX_DESCRIPTION_LENGTH && (
                    <p className="text-xs text-destructive">Description too long. Max {MAX_DESCRIPTION_LENGTH} characters.</p>
                  )}
                </div>
                
                {/* Collection field */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">
                      Collection <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <span className={`text-xs ${newNFTCollection.length > MAX_COLLECTION_LENGTH ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {newNFTCollection.length}/{MAX_COLLECTION_LENGTH}
                    </span>
                  </div>
                  <Input
                    value={newNFTCollection}
                    onChange={(e) => setNewNFTCollection(e.target.value)}
                    placeholder="e.g. My Art Series"
                    className={`bg-background/50 ${newNFTCollection.length > MAX_COLLECTION_LENGTH ? 'border-destructive' : ''}`}
                    maxLength={MAX_COLLECTION_LENGTH + 5}
                  />
                  {newNFTCollection.length > MAX_COLLECTION_LENGTH && (
                    <p className="text-xs text-destructive">Collection name too long. Max {MAX_COLLECTION_LENGTH} characters.</p>
                  )}
                </div>
                
                {/* NFT Creation Warning */}
                <NFTCreationWarning />
                
                {/* Anti-Fraud Agreement Checkbox */}
                <AntiFraudCheckbox 
                  checked={antiFraudAgreed} 
                  onCheckedChange={setAntiFraudAgreed} 
                />
                
                {/* IPFS Upload Progress */}
                {uploadingToIpfs && (
                  <div className="w-full space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Uploading to IPFS...</span>
                      <span className="text-primary font-medium">{ipfsProgress}%</span>
                    </div>
                    <Progress value={ipfsProgress} className="h-2" />
                  </div>
                )}
                
                {/* Direct Mint Button */}
                <Button
                  onClick={createNFT}
                  disabled={creatingNFT || uploadingToIpfs || !newNFTFile || isBanned || newNFTDescription.length > MAX_DESCRIPTION_LENGTH || newNFTCollection.length > MAX_COLLECTION_LENGTH || !antiFraudAgreed}
                  className="w-full"
                >
                  {creatingNFT || uploadingToIpfs ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{uploadingToIpfs ? 'Uploading to IPFS...' : 'Creating...'}</>
                  ) : isBanned ? (
                    <><AlertTriangle className="w-4 h-4 mr-2" />Suspended</>
                  ) : !antiFraudAgreed ? (
                    <><AlertTriangle className="w-4 h-4 mr-2" />Agree to Policy</>
                  ) : (
                    <><Image className="w-4 h-4 mr-2" />Mint for Free</>
                  )}
                </Button>

                {/* Automatic Price Progression */}
                <div className="mt-4 rounded-lg border border-border/50 bg-background/30 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    Automatic Price Progression
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    {[
                      { label: 'Mint', price: 0 },
                      { label: 'Sale 1', price: 1.00 },
                      { label: 'Sale 2', price: 1.26 },
                      { label: 'Sale 3', price: 1.59 },
                      { label: 'Sale 4', price: 1.99 },
                      { label: 'Sale 5', price: 2.50 },
                    ].map((step) => (
                      <div key={step.label} className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
                        <span className="text-muted-foreground">{step.label}</span>
                        <span className="font-mono font-semibold text-foreground">
                          {step.price === 0 ? 'FREE' : `$${step.price.toFixed(2)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Coins className="w-3.5 h-3.5 text-primary shrink-0" />
                    Every sale after mint burns 12% in $EMON tokens, increasing token scarcity.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Portfolio Value Summary */}
            {nfts.length > 0 && (
              <Card className="border-primary/30 bg-gradient-to-r from-primary/10 to-accent/10 backdrop-blur-sm overflow-hidden">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Portfolio Value (USD)</p>
                        <p className="text-2xl font-bold flow-gradient-text">
                          ${nfts.reduce((sum, nft) => sum + (nft.current_price || nft.price), 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">≈ EMON</p>
                      <p className="text-lg font-semibold text-primary">
                        {usdToEmon(nfts.reduce((sum, nft) => sum + (nft.current_price || nft.price), 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* My NFTs Card */}
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-primary" />
                  My NFTs
                  <span className="ml-auto text-sm font-normal text-muted-foreground">
                    {nfts.length} {nfts.length === 1 ? "item" : "items"}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <HorizontalNFTScroll
                  items={nfts}
                  maxVisible={6}
                  emptyState={
                    <div className="text-center py-12">
                      <Image className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                      <p className="text-muted-foreground">No NFTs yet</p>
                      <p className="text-sm text-muted-foreground/70">Create your first NFT above!</p>
                    </div>
                  }
                  renderItem={(nft: NFT) => (
                    <div
                      key={nft.id}
                      className="group relative rounded-xl overflow-hidden border border-border/50 bg-background/50 hover:border-primary/30 transition-all hover:shadow-lg"
                    >
                      <div className="relative">
                        {nft.image_url ? (
                          <img
                            src={nft.image_url}
                            alt={nft.name}
                            className="w-full aspect-square object-cover"
                          />
                        ) : (
                          <div className="w-full aspect-square bg-muted flex items-center justify-center">
                            <Image className="w-8 h-8 text-muted-foreground/30" />
                          </div>
                        )}
                        {/* Holding badge overlay */}
                        {nft.is_listed === false && (
                          <div className="absolute top-2 left-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/50">
                              <PauseCircle className="w-3 h-3" />
                              Holding
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-3 space-y-2">
                        {editingNFT === nft.id ? (
                          <div className="flex gap-1">
                            <Input
                              value={editNFTName}
                              onChange={(e) => setEditNFTName(e.target.value)}
                              className="h-8 text-sm"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => saveNFTEdit(nft.id)}
                              disabled={savingNFT}
                            >
                              {savingNFT ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={cancelEditNFT}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between">
                              <p className="font-semibold truncate text-primary flex-1">{nft.name}</p>
                              {/* Only show edit/delete controls for NFTs the user CREATED */}
                              {nft.creator_wallet === walletAddress && (
                                <div className="flex gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                  {/* Toggle Listing Button */}
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          onClick={() => toggleNFTListing(nft)}
                                          disabled={togglingListing === nft.id}
                                          className={`p-1 rounded transition-colors ${
                                            nft.is_listed === false 
                                              ? 'hover:bg-green-500/20 text-green-500' 
                                              : 'hover:bg-amber-500/20 text-amber-500'
                                          }`}
                                          title={nft.is_listed === false ? "Resume sale" : "Suspend sale"}
                                        >
                                          {togglingListing === nft.id ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : nft.is_listed === false ? (
                                            <PlayCircle className="w-3 h-3" />
                                          ) : (
                                            <PauseCircle className="w-3 h-3" />
                                          )}
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{nft.is_listed === false ? "Resume sale" : "Suspend sale (hold)"}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <button
                                    onClick={() => startEditNFT(nft)}
                                    className="p-1 hover:bg-muted rounded"
                                    title="Edit name"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <button
                                        className="p-1 hover:bg-destructive/20 rounded text-destructive"
                                        title="Delete NFT"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete NFT?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          This will permanently delete "{nft.name}". This action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => deleteNFT(nft)}
                                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              )}
                            </div>
                            <p className="text-xs truncate">
                              by <span className="text-orange-500 font-medium">
                                {nft.creator_name 
                                  ? nft.creator_name 
                                  : nft.creator_wallet 
                                    ? `${nft.creator_wallet.slice(0, 4)}...${nft.creator_wallet.slice(-4)}`
                                    : "Unknown Creator"}
                              </span>
                            </p>
                            <p className="text-[10px] text-muted-foreground">Owner: You</p>
                          </div>
                        )}
                        {/* Price and Earnings with USD/EMON */}
                        {(() => {
                          // Price is stored in USD
                          const usdPrice = nft.current_price || nft.price;
                          const emonPriceValue = usdToEmon(usdPrice);
                          const earnedUsd = nft.total_earned;
                          const earnedEmon = usdToEmon(earnedUsd);
                          return (
                            <>
                              <div className="flex justify-between text-xs text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <DollarSign className="w-3 h-3" />
                                  <span>${usdPrice.toFixed(2)}</span>
                                  <span className="text-primary/70">≈{emonPriceValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} EMON</span>
                                </div>
                                <span>Sales: {nft.sales_count}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <div className="text-xs text-primary font-medium flex items-center gap-1">
                                  <Coins className="w-3 h-3" />
                                  Earned: ${earnedUsd.toFixed(2)}
                                </div>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex items-center gap-1 cursor-help">
                                        {nft.image_check_status === 'approved' ? (
                                          <span className="flex items-center gap-1 text-xs text-green-500">
                                            <ShieldCheck className="w-3 h-3" /> Verified
                                          </span>
                                        ) : nft.image_check_status === 'rejected' ? (
                                          <span className="flex items-center gap-1 text-xs text-destructive">
                                            <ShieldAlert className="w-3 h-3" /> 
                                            {nft.image_lens_report?.is_duplicate ? (
                                              <><Copy className="w-3 h-3" /> Duplicate</>
                                            ) : (
                                              'Rejected'
                                            )}
                                          </span>
                                        ) : (
                                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <Clock className="w-3 h-3" /> Pending
                                          </span>
                                        )}
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      {nft.image_lens_report ? (
                                        <div className="text-xs space-y-1">
                                          <p className="font-medium">Lens Verification Report</p>
                                          <p>Duplicate Score: {nft.image_lens_report.duplicate_score}</p>
                                          {nft.image_lens_report.counts && (
                                            <>
                                              <p>Exact Matches: {nft.image_lens_report.counts.full_matches}</p>
                                              <p>Partial Matches: {nft.image_lens_report.counts.partial_matches}</p>
                                              <p>Web Pages: {nft.image_lens_report.counts.pages_with_image}</p>
                                            </>
                                          )}
                                          {nft.image_lens_report.reasons && nft.image_lens_report.reasons.length > 0 && (
                                            <p className="text-destructive">{nft.image_lens_report.reasons.join(', ')}</p>
                                          )}
                                        </div>
                                      ) : nft.image_check_status === 'pending' ? (
                                        <p className="text-xs">Verification in progress...</p>
                                      ) : (
                                        <p className="text-xs">No detailed report available</p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              {/* Interactions bar */}
                              <NftInteractionsBar
                                nftId={nft.id}
                                nftName={nft.name}
                                nftImageUrl={nft.image_url}
                                ownerWallet={walletAddress}
                                ownerName={profile?.display_name || null}
                                priceUsd={usdPrice}
                                priceEmon={emonPriceValue}
                                variant="compact"
                                className="pt-2 border-t border-border/30"
                              />
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                />
              </CardContent>
            </Card>

            {/* My Favorites Card */}
            <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  My Favorites
                  <span className="ml-auto text-sm font-normal text-muted-foreground">
                    {favorites.length} {favorites.length === 1 ? "item" : "items"}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingFavorites ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <HorizontalNFTScroll
                    items={favorites.filter(f => f.nft !== null)}
                    maxVisible={6}
                    emptyState={
                      <div className="text-center py-12">
                        <Heart className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                        <p className="text-muted-foreground">No favorites yet</p>
                        <p className="text-sm text-muted-foreground/70">Like NFTs in the marketplace to see them here!</p>
                      </div>
                    }
                    renderItem={(fav: FavoriteNFT) => {
                      const nft = fav.nft!;
                      const usdPrice = nft.current_price || nft.price;
                      const emonPriceValue = usdToEmon(usdPrice);
                      
                      return (
                        <div
                          key={fav.id}
                          className="group relative rounded-xl overflow-hidden border border-border/50 bg-background/50 hover:border-primary/30 transition-all hover:shadow-lg cursor-pointer"
                          onClick={() => navigate(`/?nft=${nft.id}`)}
                        >
                          {nft.image_url ? (
                            <img
                              src={nft.image_url}
                              alt={nft.name}
                              className="w-full aspect-square object-cover"
                            />
                          ) : (
                            <div className="w-full aspect-square bg-muted flex items-center justify-center">
                              <Image className="w-8 h-8 text-muted-foreground/30" />
                            </div>
                          )}
                          <div className="p-3 space-y-2">
                            <p className="font-semibold truncate text-primary">{nft.name}</p>
                            <p className="text-xs truncate">
                              by <span className="text-orange-500 font-medium">{nft.owner_name || nft.owner_wallet.slice(0, 8)}...</span>
                            </p>
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                <span>${usdPrice.toFixed(2)}</span>
                              </div>
                              <span className="text-primary/70">≈{emonPriceValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} EMON</span>
                            </div>
                            <NftInteractionsBar
                              nftId={nft.id}
                              nftName={nft.name}
                              nftImageUrl={nft.image_url}
                              ownerWallet={nft.owner_wallet}
                              ownerName={nft.owner_name}
                              priceUsd={usdPrice}
                              priceEmon={emonPriceValue}
                              variant="compact"
                              className="pt-2 border-t border-border/30"
                            />
                          </div>
                        </div>
                      );
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />

      {/* Post-Mint Share Modal — Mandatory */}
      <Dialog open={!!lastMintedNft} onOpenChange={() => { /* prevent dismiss */ }}>
        <DialogContent className="sm:max-w-md [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              NFT Created Successfully!
            </DialogTitle>
            <DialogDescription>
              <span className="font-semibold text-primary">Sharing on X is required</span> to keep your free mint. Share your creation to unlock it!
            </DialogDescription>
          </DialogHeader>
          {lastMintedNft && (
            <div className="flex flex-col items-center gap-4">
              {lastMintedNft.image_url && (
                <img
                  src={lastMintedNft.image_url}
                  alt={lastMintedNft.name}
                  className="w-32 h-32 rounded-lg object-cover border border-border"
                />
              )}
              <p className="text-sm font-medium text-foreground">{lastMintedNft.name}</p>
              
              {!shareCompleted ? (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => {
                    const tweetText = `I just minted "${lastMintedNft.name}" on @FLOWnft_org 🎨\n\nCheck it out 👇`;
                    const shareUrl = `https://flownft.org/share/nft/${lastMintedNft.id}`;
                    const returnUrl = `${window.location.origin}/dashboard`;

                    // Save full NFT data for re-entry restoration
                    localStorage.setItem("flow_share_pending_nft", JSON.stringify(lastMintedNft));

                    // Use in-app route (Edge function responses are shown as plain text in Phantom)
                    const bounceUrl = `/x-share-bounce?tweet=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}&name=${encodeURIComponent(lastMintedNft.name)}&return_url=${encodeURIComponent(returnUrl)}`;
                    window.location.assign(bounceUrl);
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Share on X (Required)
                </Button>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex items-center justify-center gap-2 text-green-500 text-sm font-medium py-2">
                    <Check className="w-4 h-4" />
                    Share completed! Your NFT is live.
                  </div>
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={() => {
                      localStorage.removeItem("flow_share_pending_nft");
                      setLastMintedNft(null);
                      setShareCompleted(false);
                    }}
                  >
                    Done
                  </Button>
                </div>
              )}

              {!shareCompleted && (
                <p className="text-xs text-muted-foreground text-center">
                  After sharing on X, come back here and confirm.
                </p>
              )}

              {!shareCompleted && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground text-xs"
                  onClick={() => {
                    localStorage.removeItem("flow_share_pending_nft");
                    setShareCompleted(true);
                  }}
                >
                  I've already shared ✓
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
